import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Lock, LogOut, Trash2, FileUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

function LoginForm({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
            onSuccess();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-[70vh] items-center justify-center px-4 py-20" data-testid="admin-login-view">
            <form onSubmit={submit} className="w-full max-w-sm border border-slate-200 bg-white p-8" data-testid="admin-login-form">
                <div className="flex h-11 w-11 items-center justify-center border border-navy bg-navy text-white">
                    <Lock className="h-5 w-5" />
                </div>
                <h1 className="mt-5 text-2xl font-bold tracking-tight text-navy">Admin Login</h1>
                <p className="mt-2 text-[13px] text-slate-500">Sign in to view customer enquiries.</p>
                <div className="mt-6 space-y-4">
                    <div>
                        <label className="mb-1.5 block font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-slate-500" htmlFor="admin-email">Email</label>
                        <Input id="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" className="h-11 rounded-none border-slate-300" />
                    </div>
                    <div>
                        <label className="mb-1.5 block font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-slate-500" htmlFor="admin-password">Password</label>
                        <Input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" className="h-11 rounded-none border-slate-300" />
                    </div>
                </div>
                {error && (
                    <p className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700" data-testid="admin-login-error">
                        {error}
                    </p>
                )}
                <Button type="submit" disabled={busy} data-testid="admin-login-btn" className="mt-6 h-11 w-full rounded-none border border-brand-red bg-brand-red text-[13px] font-semibold uppercase tracking-wider text-white hover:bg-brand-redhover">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
                </Button>
            </form>
        </div>
    );
}

function Dashboard({ onLogout }) {
    const [list, setList] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
            setList(data);
        } catch (e) {
            if (e.response?.status === 401) {
                onLogout();
                return;
            }
            setError(formatApiErrorDetail(e.response?.data?.detail));
            setList([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const remove = async (id) => {
        try {
            await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
            setList((l) => (l ? l.filter((e) => e.id !== id) : l));
        } catch (e) {
            if (e.response?.status === 401) onLogout();
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
        } catch (e) {
            /* ignore */
        }
        onLogout();
    };

    return (
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="admin-dashboard">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-red">Admin Panel</p>
                    <h1 className="mt-2 text-3xl font-bold tracking-tight text-navy">Customer Enquiries</h1>
                </div>
                <Button onClick={logout} variant="outline" data-testid="admin-logout-btn" className="rounded-none border-navy text-navy hover:bg-navy hover:text-white">
                    <LogOut className="h-4 w-4" /> Sign Out
                </Button>
            </div>

            {error && <p className="mt-6 border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700" data-testid="admin-error">{error}</p>}

            {list === null ? (
                <div className="flex items-center gap-3 py-20 text-slate-500" data-testid="admin-loading">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading enquiries...
                </div>
            ) : list.length === 0 ? (
                <p className="mt-10 border border-slate-200 bg-slate-50 p-8 text-[14px] text-slate-500" data-testid="admin-empty">
                    No enquiries yet. Enquiries submitted through the website will appear here.
                </p>
            ) : (
                <div className="mt-8 overflow-x-auto border border-slate-200" data-testid="admin-enquiries-table">
                    <table className="w-full min-w-[1100px] border-collapse text-left">
                        <thead>
                            <tr className="bg-navy text-white">
                                {["Date", "Name", "Company", "Phone", "Email", "PCB Type", "Requirement", "Qty", "Message", "Gerber", ""].map((h) => (
                                    <th key={h} className="px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((e) => (
                                <tr key={e.id} className="border-b border-slate-100 bg-white align-top text-[13px] text-slate-700" data-testid="admin-enquiry-row">
                                    <td className="px-4 py-3 font-mono text-[11.5px] text-slate-500">{e.created_at ? e.created_at.slice(0, 10) : "-"}</td>
                                    <td className="px-4 py-3 font-semibold text-navy">{e.name}</td>
                                    <td className="px-4 py-3">{e.company || "-"}</td>
                                    <td className="px-4 py-3"><a href={`tel:${e.phone}`} className="text-navy hover:text-brand-red">{e.phone}</a></td>
                                    <td className="px-4 py-3"><a href={`mailto:${e.email}`} className="text-navy hover:text-brand-red">{e.email}</a></td>
                                    <td className="px-4 py-3">{e.pcb_type || "-"}</td>
                                    <td className="px-4 py-3">{e.pcb_requirement || "-"}</td>
                                    <td className="px-4 py-3">{e.quantity || "-"}</td>
                                    <td className="max-w-[220px] px-4 py-3">{e.message || "-"}</td>
                                    <td className="px-4 py-3">
                                        {e.gerber ? (
                                            <span className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500" data-testid="admin-gerber-file">
                                                <FileUp className="h-3.5 w-3.5 text-brand-red" /> {e.gerber.original_filename}
                                            </span>
                                        ) : "-"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => remove(e.id)}
                                            aria-label="Delete enquiry"
                                            data-testid={`admin-enquiry-delete-${e.id.slice(0, 8)}`}
                                            className="p-1.5 text-slate-400 transition-colors hover:text-brand-red"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function Admin() {
    const [checking, setChecking] = useState(true);
    const [authed, setAuthed] = useState(false);

    const check = async () => {
        try {
            await axios.get(`${API}/admin/me`, { withCredentials: true });
            setAuthed(true);
        } catch (e) {
            try {
                await axios.post(`${API}/admin/refresh`, {}, { withCredentials: true });
                await axios.get(`${API}/admin/me`, { withCredentials: true });
                setAuthed(true);
            } catch (e2) {
                setAuthed(false);
            }
        } finally {
            setChecking(false);
        }
    };

    useEffect(() => {
        check();
    }, []);

    if (checking) {
        return (
            <div className="flex min-h-[70vh] items-center justify-center text-slate-500" data-testid="admin-checking">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }
    return authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
    ) : (
        <LoginForm onSuccess={() => setAuthed(true)} />
    );
}
